import api from 'balkerne-core/api'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import type { QueryClient } from 'react-query'
import type { Adaptation, OperationShape, Transform, TransformShape } from '../types/adaptations'

export type { OperationFamily, OperationShape, Transform } from '../types/adaptations'

type UUID = string
type QueryOpts = any
type MutationHandlers<V> = {
  onSuccess?: (response: any, variables: V) => void
  onError?: (error: any) => void
}
type AddAdaptationVars = { propertyId: number; transformId: number }
type RemoveAdaptationVars = { propertyId: number; adaptationId: number }
type CreateProfileVars = {
  slotId: number
  propertyTypeId: UUID
  transformId: number | null
  description?: string | null
}
type DeleteProfileVars = { profileId: number }
type UpdateProfileTransformVars = { profileId: number; transformId: number }
export type CreateBaseTransformVars = {
  slotId: number
  label: string
  description?: string | null
  shapeType: OperationShape
  params: TransformShape
  copiedFromTransformId?: number | null
}

/**
 * Helper to nuke all climate analytics query caches
 */
const invalidateAllCAQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    predicate: (q: any) => {
      const key = Array.isArray(q.queryKey) ? q.queryKey[0] : q.queryKey
      return typeof key === 'string' && key.startsWith('/analytics/ca')
    },
  })
}

/**
 * -----------------------------------------------------------------------------
 * Transforms
 */

/**
 * GET /analytics/ca/transforms w/ query params
 * List transforms applicable to a given model slot.
 */
export const useBaseTransforms = (
  slotId: number | null,
  params: { labelSubstr?: string; limit?: number; offset?: number } = {},
  opts: QueryOpts = {},
) => {
  const enabled = typeof slotId === 'number'
  const base = '/analytics/ca/transforms/base'
  const qs = new URLSearchParams()
  if (typeof slotId === 'number') qs.set('slot_id', String(slotId))
  if (params.labelSubstr) qs.set('label_substr', params.labelSubstr)
  if (typeof params.limit === 'number') qs.set('limit', String(params.limit))
  if (typeof params.offset === 'number') qs.set('offset', String(params.offset))

  const path = `${base}${qs.toString() ? `?${qs.toString()}` : ''}`
  const fetcher = async (): Promise<Transform[]> => (await api.get(path)).data
  const { data, ...rest } = useQuery(path, fetcher, { enabled, ...opts })
  return { ...rest, data: data ?? [] }
}

/**
 * GET /analytics/ca/transforms/:id
 * Fetch a single transform by ID (e.g., to show a slot's default_transform_id).
 */
export const useTransformById = (id: number | null | undefined, opts: QueryOpts = {}) => {
  const enabled = typeof id === 'number' && id > 0
  const path = `/analytics/ca/transforms/${enabled ? id : ''}`
  const fetcher = async (): Promise<Transform> => (await api.get(path)).data
  const { data, ...rest } = useQuery(path, fetcher, { enabled, ...opts })
  return { ...rest, data: (data ?? null) as Transform | null }
}

/** Create an immutable organisation transform scoped to one model slot. */
export const useCreateBaseTransform = (props: MutationHandlers<CreateBaseTransformVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<Transform, unknown, CreateBaseTransformVars>(
    async ({ slotId, label, description, shapeType, params, copiedFromTransformId }) => {
      const response = await api.post('/analytics/ca/transforms/base', {
        slot_id: slotId,
        label,
        description: description ?? null,
        shape_type: shapeType,
        params,
        copied_from_transform_id: copiedFromTransformId ?? null,
      })
      return response.data
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        if (onError) onError(error)
      },
    },
  )
}

/**
 * -----------------------------------------------------------------------------
 * Adaptations
 */

/**
 * GET /ca/adaptations?hazard_id=...
 * List available adaptations (a.k.a. transforms) for a hazard
 */
export const useTransformsForAdaptations = (hazardId: number | undefined) => {
  const enabled = Boolean(hazardId)
  const path = `/analytics/ca/adaptations${hazardId ? `?hazard_id=${encodeURIComponent(hazardId)}` : ''}`
  const fetcher = async () => (await api.get(path)).data
  const { data, ...rest } = useQuery<Transform[]>(path, fetcher, { enabled })
  return { ...rest, data: data ?? [] }
}

/**
 * GET /ca/locations/:id/adaptations?hazard_id=...
 * Get property's adaptations for a hazard
 */
export const usePropertyAdaptations = (propertyId: number, hazardId: number | undefined) => {
  const enabled = Boolean(propertyId && hazardId)
  const queryParams = new URLSearchParams()
  if (hazardId) queryParams.set('hazard_id', String(hazardId))
  const params = queryParams.toString() ? `?${queryParams.toString()}` : ''
  const path = `/analytics/ca/locations/${propertyId}/adaptations/${params}`
  const fetcher = async () => (await api.get(path)).data
  const { data, ...rest } = useQuery<Adaptation[]>(path, fetcher, { enabled })
  return { ...rest, data: data ?? [] }
}

/**
 * POST /ca/locations/:id/adaptations
 * Add an adaptation (transform) to a property
 *
 * mutate({ propertyId, transformId })
 */
export const useAddPropertyAdaptation = (props: MutationHandlers<AddAdaptationVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<any, unknown, AddAdaptationVars>(
    async ({ propertyId, transformId }) => {
      await api.post(`/analytics/ca/locations/${propertyId}/adaptations`, { transform_id: transformId })
      return { ok: true }
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        console.error('error', error)
        if (onError) onError(error)
      },
    },
  )
}

/**
 * DELETE /ca/locations/:id/adaptations/:adaptation_id
 * Remove an adaptation from a property.
 *
 * mutate({ propertyId, adaptationId })
 */
export const useDeletePropertyAdaptation = (props: MutationHandlers<RemoveAdaptationVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<any, unknown, RemoveAdaptationVars>(
    async ({ propertyId, adaptationId }) => {
      await api.delete(`/analytics/ca/locations/${propertyId}/adaptations/${adaptationId}`)
      return { ok: true }
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        console.error('error', error)
        if (onError) onError(error)
      },
    },
  )
}

/**
 * -----------------------------------------------------------------------------
 * Models
 */

/**
 * GET /ca/models
 * Get models (with slots).
 */
export const useImpactModels = (opts: QueryOpts = {}) => {
  const path = '/analytics/ca/models'
  const fetcher = async () => (await api.get(path)).data
  const { data, ...rest } = useQuery(path, fetcher, { ...opts })
  return { ...rest, data: data ?? [] }
}

/**
 * GET /ca/models/:ref/profiles
 * Get a model's slot profiles (org-scoped).
 */
export const useImpactModelProfiles = (modelUID: string, opts: QueryOpts = {}) => {
  const enabled = Boolean(modelUID)
  const path = `/analytics/ca/models/${encodeURIComponent(modelUID ?? '')}/profiles`
  const fetcher = async () => (await api.get(path)).data
  const { data, ...rest } = useQuery(path, fetcher, { enabled, ...opts })
  // Shape is unknown; return as-is (null until loaded)
  return { ...rest, data: data ?? null }
}

/**
 * -----------------------------------------------------------------------------
 * Profiles
 */

/**
 * POST /ca/profiles
 * Create a model slot profile.
 *
 * mutate({ slotId, propertyTypeId, description })
 */
export const useCreateSlotProfile = (props: MutationHandlers<CreateProfileVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<any, unknown, CreateProfileVars>(
    async ({ slotId, propertyTypeId, description, transformId }) => {
      await api.post('/analytics/ca/profiles', {
        slot_id: slotId,
        property_type_id: propertyTypeId,
        transform_id: transformId,
        description: description ?? null,
      })
      return { ok: true }
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        console.error('error', error)
        if (onError) onError(error)
      },
    },
  )
}

/**
 * DELETE /ca/profiles/:profile_id
 *
 * mutate({ profileId })
 */
export const useDeleteSlotProfile = (props: MutationHandlers<DeleteProfileVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<any, unknown, DeleteProfileVars>(
    async ({ profileId }) => {
      await api.delete(`/analytics/ca/profiles/${profileId}`)
      return { ok: true }
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        console.error('error', error)
        if (onError) onError(error)
      },
    },
  )
}

/**
 * PUT /ca/profiles/:profile_id
 * Update a profile's transform.
 *
 * mutate({ profileId, transformId })
 */
export const useUpdateSlotProfileTransform = (props: MutationHandlers<UpdateProfileTransformVars> = {}) => {
  const { onSuccess, onError } = props
  const queryClient = useQueryClient()

  return useMutation<any, unknown, UpdateProfileTransformVars>(
    async ({ profileId, transformId }) => {
      await api.put(`/analytics/ca/profiles/${profileId}`, { transform_id: transformId })
      return { ok: true }
    },
    {
      onSuccess: (response, variables) => {
        invalidateAllCAQueries(queryClient)
        if (onSuccess) onSuccess(response, variables)
      },
      onError: error => {
        console.error('error', error)
        if (onError) onError(error)
      },
    },
  )
}
